/**
 * Centralized timezone utilities for JSDC application.
 * All timestamps are displayed in IST (UTC+05:30).
 * Date format: DD/MM/YY
 * Time format: HH:mm (24-hour)
 */

// IST timezone identifier
const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Format date to IST date string (DD/MM/YY)
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string} Formatted date string
 */
export const formatISTDate = (dateInput) => {
  if (!dateInput) return '-';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    
    // Format in IST timezone
    const options = {
      timeZone: IST_TIMEZONE,
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    };
    
    const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);
    const day = parts.find(p => p.type === 'day')?.value || '00';
    const month = parts.find(p => p.type === 'month')?.value || '00';
    const year = parts.find(p => p.type === 'year')?.value || '00';
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

/**
 * Format date to IST time string (HH:mm - 24 hour format)
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string} Formatted time string
 */
export const formatISTTime = (dateInput) => {
  if (!dateInput) return '-';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    
    // Format in IST timezone - 24 hour format
    const options = {
      timeZone: IST_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  } catch (error) {
    console.error('Error formatting time:', error);
    return '-';
  }
};

/**
 * Format date to IST full datetime string (DD/MM/YY HH:mm)
 * @param {string|Date} dateInput - ISO string or Date object
 * @returns {string} Formatted datetime string
 */
export const formatISTDateTime = (dateInput) => {
  if (!dateInput) return '-';
  
  const dateStr = formatISTDate(dateInput);
  const timeStr = formatISTTime(dateInput);
  
  if (dateStr === '-' || timeStr === '-') return '-';
  
  return `${dateStr} ${timeStr}`;
};

/**
 * Format date for display with optional time
 * @param {string|Date} dateInput - ISO string or Date object
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} Formatted string
 */
export const formatISTDisplay = (dateInput, includeTime = false) => {
  return includeTime ? formatISTDateTime(dateInput) : formatISTDate(dateInput);
};
